import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, Info, Shield } from "lucide-react";

/**
 * PreSettlementCalculator — interactive widget embedded inside the launch
 * article "Pre-Settlement Funding Calculator: Estimate Your Advance + Payoff
 * Cost".  Renders inline anywhere a blog post body contains the literal
 * placeholder token "{{CALCULATOR_WIDGET}}".
 *
 * Math model:
 *   advance        = amount the plaintiff receives today
 *   monthly_rate   = compounded monthly (industry-standard tiered)
 *   case_months    = time-to-settlement
 *   payoff(t)      = advance * (1 + rate)^t  (capped at max_tiers)
 *
 * Pure UI — no API call. The numbers are illustrative and Instabridge's true
 * underwriting depends on case strength, principal cap, etc.
 */
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtPrecise = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

function compound(principal, monthlyRate, months, maxTiers = 6) {
  const tiers = Math.min(months, maxTiers);
  return principal * Math.pow(1 + monthlyRate, tiers);
}

export default function PreSettlementCalculator() {
  const [advance, setAdvance] = useState(5000);
  const [months, setMonths] = useState(9);
  const [rateBps, setRateBps] = useState(380); // 3.80% per month

  const monthlyRate = rateBps / 10000;
  const payoff = useMemo(() => compound(advance, monthlyRate, months), [advance, monthlyRate, months]);
  const interest = payoff - advance;
  const apr = useMemo(() => Math.pow(1 + monthlyRate, 12) - 1, [monthlyRate]);

  // Sample settlement scenarios to show real-world context
  const scenarios = [3, 6, 12, 18, 24].map((m) => ({
    months: m,
    payoff: compound(advance, monthlyRate, m),
  }));

  return (
    <div className="not-prose my-10 rounded-3xl border border-[#E5E7EB] bg-gradient-to-br from-white via-[#F9FAFB] to-[#F0F6FF] overflow-hidden shadow-sm" data-testid="psf-calculator">
      <div className="px-7 sm:px-10 py-7 sm:py-9 border-b border-[#E5E7EB] bg-white/50 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0066FF] to-[#001A3D] text-white flex items-center justify-center">
            <Calculator className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-medium">Interactive estimate</div>
            <div className="font-display text-xl text-[#0A2540] font-semibold">Pre-Settlement Funding Calculator</div>
          </div>
        </div>
        <p className="mt-3 text-sm text-[#374151] max-w-2xl">
          Move the sliders to see how an advance grows over time. The math below mirrors the industry standard tiered model — your real Instabridge quote may be lower.
        </p>
      </div>

      <div className="px-7 sm:px-10 py-7 sm:py-9 grid lg:grid-cols-[1.1fr_1fr] gap-9">
        {/* Inputs */}
        <div className="space-y-6">
          <Slider
            label="Advance amount" testid="psf-advance"
            value={advance} onChange={setAdvance}
            min={500} max={25000} step={500}
            display={fmt(advance)}
          />
          <Slider
            label="Estimated months until settlement" testid="psf-months"
            value={months} onChange={setMonths}
            min={1} max={24} step={1}
            display={`${months} month${months !== 1 ? "s" : ""}`}
          />
          <Slider
            label="Monthly rate (illustrative)" testid="psf-rate"
            value={rateBps} onChange={setRateBps}
            min={250} max={500} step={10}
            display={`${(rateBps / 100).toFixed(2)}% / month`}
          />

          <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex items-start gap-2.5 text-xs text-[#6B7280] leading-relaxed">
            <Info className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
            <div>
              Most pre-settlement funding uses a <strong className="text-[#0A2540]">tiered (capped) compound model</strong>. After ~6 monthly tiers (varies by funder) most contracts <em>stop</em> accruing — your payoff is locked in. Instabridge caps at 6 tiers.
            </div>
          </div>
        </div>

        {/* Outputs */}
        <div className="space-y-4">
          <div className="bg-[#0A2540] text-white rounded-2xl p-6">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 mb-2">Estimated payoff at settlement</div>
            <div className="font-display text-4xl sm:text-5xl font-semibold tabular-nums" data-testid="psf-payoff">{fmt(payoff)}</div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <div>
                <div className="text-white/60 text-xs">Advance</div>
                <div className="tabular-nums font-medium">{fmt(advance)}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-white/40" />
              <div className="text-right">
                <div className="text-white/60 text-xs">Cost of funding</div>
                <div className="tabular-nums font-medium" data-testid="psf-cost">{fmtPrecise(interest)}</div>
              </div>
              <div className="text-right">
                <div className="text-white/60 text-xs">Effective APR</div>
                <div className="tabular-nums font-medium">{(apr * 100).toFixed(1)}%</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#F3F4F6] text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-medium">If your case takes…</div>
            <div className="divide-y divide-[#F3F4F6]">
              {scenarios.map((s) => (
                <div key={s.months} className="flex items-center justify-between px-5 py-2.5 text-sm">
                  <div className="text-[#374151]">{s.months} month{s.months !== 1 ? "s" : ""}</div>
                  <div className="tabular-nums font-medium text-[#0A2540]">{fmt(s.payoff)}</div>
                </div>
              ))}
            </div>
          </div>

          <Link
            to="/apply"
            className="block w-full text-center bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium rounded-full h-12 leading-[3rem] transition-colors"
            data-testid="psf-cta-apply"
          >
            Get my real quote in 24 hours <ArrowRight className="inline w-4 h-4 ml-1.5 -mt-0.5" />
          </Link>
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#6B7280]">
            <Shield className="w-3 h-3" /> Non-recourse · Owe nothing if you lose · Soft credit check only
          </div>
        </div>
      </div>
    </div>
  );
}

function Slider({ label, value, onChange, min, max, step, display, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-sm font-medium text-[#374151]">{label}</label>
        <span className="font-display text-lg text-[#0A2540] font-semibold tabular-nums" data-testid={`${testid}-display`}>{display}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="psf-slider w-full"
      />
      <div className="flex justify-between text-[10px] text-[#9CA3AF] mt-1 font-mono">
        <span>{typeof min === "number" && min < 100 ? min : fmt(min)}</span>
        <span>{typeof max === "number" && max < 100 ? max : fmt(max)}</span>
      </div>
    </div>
  );
}
